import React from 'react';
import Helmet from 'react-helmet';
import { SNAPASK_SITE_ENDPOINT } from 'config';
import Layout from 'components/Layout';

import { getUserIsAuthenticated } from 'reduxModules/user/selector';
import { fetchCourse, fetchLearningCourse } from 'reduxModules/course/action';
import {
  fetchCourseFlow,
  fetchLearningCourseFlow,
} from 'reduxModules/course/saga';

import { getCourseById } from 'reduxModules/course/selector';

import { fetchUpsellBanner } from 'reduxModules/upsellBanner/action';
import { fetchCheckoutCollectionList } from 'reduxModules/checkoutCollection/action';
import { fetchCheckoutCollectionListFlow } from 'reduxModules/checkoutCollection/saga';

import { fetchUpsellBannerFlow } from 'reduxModules/upsellBanner/saga';
import { COURSE } from 'constants/upsellBannerPage';

import Course from './Course';
import { generateOfficialSiteURL } from 'reduxModules/navigation/selector';
import {
  COURSE_GROUP_TEMPLATE,
  HOME,
  COURSE as COURSE_PAGE,
} from 'constants/routingPath';
import * as contentTypes from 'constants/contentTypes';

import {
  createBreadcrumbForJsonLD,
  getOfferInfoForJsonLD,
  getRatingForJsonLD,
} from 'utils/jsonLD';
import { getContentCheckoutCollectionList } from 'reduxModules/checkoutCollection/selector';

async function action(context) {
  const { store, params, pathname, i18n } = context;

  const isAuthenticated = getUserIsAuthenticated(store.getState());

  await Promise.all([
    store.runSagas(fetchCourseFlow, fetchCourse({ id: params.id })).toPromise(),
    store
      .runSagas(fetchUpsellBannerFlow, fetchUpsellBanner({ page: COURSE }))
      .toPromise(),
    store.runSagas(
      fetchCheckoutCollectionListFlow,
      fetchCheckoutCollectionList({
        contentType: contentTypes.COURSE,
        contentId: params.id,
      }),
    ),
    isAuthenticated &&
      store
        .runSagas(fetchLearningCourseFlow, fetchLearningCourse())
        .toPromise(),
  ]);

  const courseInfo = getCourseById(params.id)(store.getState());

  if (!courseInfo) {
    return undefined;
  }

  await i18n.loadNamespaces(['common', 'navbar']);

  const contentList = getContentCheckoutCollectionList(
    contentTypes.COURSE,
    params.id,
  )(store.getState());

  const pictureUrl = courseInfo.picture.originalUrl;
  const getOfficialSiteURL = generateOfficialSiteURL(store.getState());

  const breadcrumbList = [
    {
      link: getOfficialSiteURL(HOME),
      label: i18n.t('common:common.breadcrumb.home'),
    },
    {
      link: getOfficialSiteURL(COURSE_PAGE),
      label: i18n.t('navbar:navbar.link.course'),
    },
    {
      link: getOfficialSiteURL(COURSE_GROUP_TEMPLATE, {
        id: courseInfo.leadGroup.id,
      }),
      label: courseInfo.leadGroup.name,
    },
  ];

  const currentPage = {
    link: pathname,
    label: courseInfo.title,
  };

  return {
    title: courseInfo.metaTitle,
    description: courseInfo.metaDescription,
    ogTags: {
      title: courseInfo.metaTitle,
      description: courseInfo.metaDescription,
      image: pictureUrl,
      type: 'website',
      url: `${SNAPASK_SITE_ENDPOINT}${pathname}`,
    },
    jsonLDList: [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        image: pictureUrl,
        name: courseInfo.title,
        description: courseInfo.description,
        publisher: {
          '@type': 'Organization',
          name: 'Snapask',
        },
        provider: {
          '@type': 'Person',
          name: courseInfo?.instructors[0]?.name,
          image: courseInfo?.instructors[0]?.avatarUrl,
        },
        ...getOfferInfoForJsonLD(courseInfo.checkoutCollection, contentList),
        ...getRatingForJsonLD(courseInfo.averageRate, courseInfo.ratings),
      },
      createBreadcrumbForJsonLD(
        [...breadcrumbList, currentPage],
        'course detail page',
      ),
    ],
    chunks: ['course'],
    component: (
      <Layout>
        {+params.lessonId === 0 && (
          <Helmet>
            {/*
              https://developers.google.com/search/docs/advanced/crawling/301-redirects
              google search redirect
            */}
            <meta
              httpEquiv="refresh"
              content={`0; url=https://snapask.com/course/${params.id}`}
            />
          </Helmet>
        )}
        {+params.lessonId !== 0 && courseInfo && (
          <Course
            id={params.id}
            lessonId={+params.lessonId}
            breadcrumbList={breadcrumbList}
          />
        )}
      </Layout>
    ),
  };
}

export default action;
